// Where a floating box opens so that all of it is on screen (dioxus-plan.md D425).
//
// A menu, a popover, a submenu, a card at a point: each has a place it *wants*
// — a context menu hangs from the pointer down and to the right, a submenu
// stands to the right of its row, the send button's menu grows up and to the
// left of the corner it lives in — and each is wrong the moment that place
// runs off the edge of the screen, because a menu whose last row is under the
// taskbar is a menu whose last row cannot be pressed. So the place it wants is
// a *first choice*, and this file is the rest of the list: the box is measured
// where it is drawn, the wanted place is checked against the viewport, and
// when it does not fit the other places around the anchor are tried in turn
// until one does. Only when none of them does is the box left where it
// wanted to be, pushed in from the edge as far as its own size allows — which
// is what every menu here did before there was a list.
//
//     window.__echoPlace(anchor, size, bounds, want)
//       anchor  {x, y, w, h}  what the box opens against, in viewport pixels —
//                            a pointer is a box of no size
//       size    {w, h}        the box, as laid out (offsetWidth / offsetHeight)
//       bounds  {w, h}        the viewport (innerWidth / innerHeight)
//       want    {side, align, offset, edge, lift, avoid}
//               side    top | right | bottom | left — which side of the anchor
//               align   start | center | end — where along that side; `start`
//                       lines the box's left (or top) edge up with the anchor's
//               offset  the gap between anchor and box, 0 when the anchor is
//                       the pointer, the row gap for a submenu
//               edge    how close to the viewport's edge a box may come (8)
//               lift    how far above the anchor the box's *top* starts while
//                       that top is what is lined up with the anchor — the
//                       height of a row of reactions riding over a menu, so
//                       the menu itself still opens at the pointer
//               avoid   boxes {x, y, w, h} a place must not overlap to fit —
//                       the tips already up when twenty go up at once (the
//                       Ctrl mode, tooltips-plan.md D741); none by default
//       →       {x, y, side, align, fits}
//
// The order the places are tried in is the order a reader would expect them
// in: the side asked for, in the alignment asked for; the same side with the
// box slid the other way along it; then the same three across from the
// anchor; then the two sides beside. A context menu near the bottom of the
// screen therefore opens *upwards* from the pointer, as the native one does,
// one near the right edge opens leftwards, as Telegram's does, and one in the
// corner does both — rather than the box being dragged back onto the screen
// by its overflow until the pointer sat somewhere in its middle. For a box
// opened against a point the twelve places collapse into the eight around it,
// which is the list the owner gave.
//
// Pure, and run after every change that could change the answer — the box
// arriving, its content growing, the window resizing — by whoever draws the
// box: the layer's placement script on the desktop (`floating.rs`), the
// `usePlaced` hook on the web (`react/lib/placement.ts`); the desktop's own
// card is placed by the same arithmetic in Rust (`floating::fit`), where its
// tests are. Shared by both clients for the reason `ink.js` is: one rule,
// one answer, whichever window asks.
(function () {
  "use strict";

  if (typeof window === "undefined" || window.__echoPlace) return;

  var ACROSS = { top: "bottom", bottom: "top", left: "right", right: "left" };

  // The sides in the order they are tried: asked for, across, then beside.
  function sides(side) {
    var beside = side === "top" || side === "bottom" ? ["right", "left"] : ["bottom", "top"];
    return [side, ACROSS[side], beside[0], beside[1]];
  }

  // The alignments in the order they are tried along each side: asked for,
  // the other end, then the middle — a centred box over a pointer puts the
  // pointer in the middle of the box's edge, which is the least like a menu.
  function aligns(align) {
    if (align === "center") return ["center", "start", "end"];
    return [align, align === "start" ? "end" : "start", "center"];
  }

  function along(align, from, span, extent) {
    if (align === "start") return from;
    if (align === "end") return from + span - extent;
    return from + span / 2 - extent / 2;
  }

  // The box's top-left for one place.
  function at(anchor, size, side, align, offset, lift) {
    var x, y;
    if (side === "top" || side === "bottom") {
      y = side === "top" ? anchor.y - offset - size.h : anchor.y + anchor.h + offset - lift;
      x = along(align, anchor.x, anchor.w, size.w);
    } else {
      x = side === "left" ? anchor.x - offset - size.w : anchor.x + anchor.w + offset;
      y = along(align, anchor.y, anchor.h, size.h);
      if (align === "start") y -= lift;
    }
    return { x: x, y: y };
  }

  function fits(p, size, bounds, edge, avoid) {
    if (p.x < edge || p.y < edge || p.x + size.w > bounds.w - edge || p.y + size.h > bounds.h - edge) {
      return false;
    }
    for (var i = 0; avoid && i < avoid.length; i++) {
      var b = avoid[i];
      if (p.x < b.x + b.w && b.x < p.x + size.w && p.y < b.y + b.h && b.y < p.y + size.h) return false;
    }
    return true;
  }

  // Pushed in from the far edges first, then held off the near ones: a box
  // wider than the viewport keeps its left edge inside, where its text starts.
  function pushed(p, size, bounds, edge) {
    var x = Math.min(p.x, bounds.w - edge - size.w);
    var y = Math.min(p.y, bounds.h - edge - size.h);
    return { x: Math.max(x, edge), y: Math.max(y, edge) };
  }

  function rounded(p, side, align, ok) {
    return { x: Math.round(p.x), y: Math.round(p.y), side: side, align: align, fits: ok };
  }

  window.__echoPlace = function (anchor, size, bounds, want) {
    want = want || {};
    var side = want.side || "bottom";
    var align = want.align || "start";
    var offset = want.offset || 0;
    var edge = want.edge == null ? 8 : want.edge;
    var lift = want.lift || 0;
    var avoid = want.avoid || null;
    var ss = sides(side);
    var as = aligns(align);
    for (var i = 0; i < ss.length; i++) {
      for (var j = 0; j < as.length; j++) {
        var p = at(anchor, size, ss[i], as[j], offset, lift);
        if (fits(p, size, bounds, edge, avoid)) return rounded(p, ss[i], as[j], true);
      }
    }
    var wanted = at(anchor, size, side, align, offset, lift);
    return rounded(pushed(wanted, size, bounds, edge), side, align, false);
  };
})();
